/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext, replaceEqualDeep } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetPositionsRow, GetProfilePortfolioSnapshotRow, GetPositionsSummaryRow, GetBackerRewardsRow, GetBackerRewardsSummaryRow, GetClaimableRewardsRow, GetUserRow, GetUserPnlChartRow, GetUserStreakRow, GetFollowsCountsRow, GetFollowsCheckRow, GetFollowsRow, GetUserActivityRow, GetGiftsRow, GetNotificationPrefsRow, GetUserMarketsRow, GetCreatorStatsRow, GetMyPayoutWalletRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { AmmUserActivitySchema, BackerFeeClaimEventSchema, GiftEventSchema, MarketBackingSchema, MarketBoostSchema, MarketPrizeClaimSchema, MarketTradeSchema, UserNotificationSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── live re-rank · shared comparators ─────────────────────────────────────

function _toBigIntOrZero(v: unknown): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? BigInt(Math.trunc(v)) : 0n;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '' || !/^-?\d+$/.test(t)) return 0n;
    try {
      return BigInt(t);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function _cmpD6(a: unknown, b: unknown): number {
  const av = _toBigIntOrZero(a);
  const bv = _toBigIntOrZero(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}

function _cmpNum(a: unknown, b: unknown): number {
  const av = typeof a === 'number' ? a : Number(a);
  const bv = typeof b === 'number' ? b : Number(b);
  const an = Number.isFinite(av) ? av : 0;
  const bn = Number.isFinite(bv) ? bv : 0;
  return an < bn ? -1 : an > bn ? 1 : 0;
}

function _cmpStr(a: unknown, b: unknown): number {
  const av = a == null ? '' : String(a);
  const bv = b == null ? '' : String(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}


// ─── useGetPositions ────────────────────────────────────────────────────────────

export interface useGetPositionsArgs { readonly userId: string; readonly walletPubkeys?: readonly string[]; readonly market?: string; readonly marketPubkeys?: readonly string[]; readonly outcomeMints?: string; readonly positionType?: string; readonly status?: string; readonly includeZero?: string; readonly sortBy?: string; readonly sortOrder?: string; readonly limit?: number | string; readonly viewportGated?: boolean; readonly fields?: readonly ('marketType' | 'partnerCreatorId' | 'associatedCreator' | 'positionId' | 'positionType' | 'wallet' | 'walletQuoteBalances' | 'market' | 'marketTitle' | 'marketStatus' | 'outcomeIndex' | 'outcomeMint' | 'outcomeLabel' | 'outcomeImageUrl' | 'outcomeImageNsfw' | 'balance' | 'claimRightsBalanceD6' | 'avgEntryPriceD9' | 'currentPriceD9' | 'costBasisD6' | 'remainingCostBasisGrossD6' | 'remainingCostBasisNetD6' | 'realizedPnlGrossD6' | 'realizedPnlNetD6' | 'realizedPenaltyD6' | 'realizedPnlD6' | 'realizedCostBasisGrossD6' | 'realizedCostBasisNetD6' | 'realizedCostBasisD6' | 'unrealizedPnlD6' | 'tradeCount' | 'isOpen' | 'resultState' | 'prizeClaimableD6' | 'prizeClaimedD6' | 'prizeState' | 'pnl' | 'pnlUnavailableReason')[]; }


async function _useGetPositionsQueryFn(
  args: useGetPositionsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetPositionsRow>> {
  const url = `/v1/users/${args.userId}/positions`;
  const params = new URLSearchParams();

  if (args.walletPubkeys !== undefined) params.set('walletPubkeys', String(args.walletPubkeys));
  if (args.market !== undefined) params.set('market', String(args.market));
  if (args.marketPubkeys !== undefined) params.set('marketPubkeys', String(args.marketPubkeys));
  if (args.outcomeMints !== undefined) params.set('outcomeMints', String(args.outcomeMints));
  if (args.positionType !== undefined) params.set('positionType', String(args.positionType));
  if (args.status !== undefined) params.set('status', String(args.status));
  if (args.includeZero !== undefined) params.set('includeZero', String(args.includeZero));
  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? Number(args.limit ?? 20)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetPositions',
    routeTemplate: '/v1/users/{userId}/positions',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetPositionsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
    _sort: { by: args.sortBy ?? null, order: args.sortOrder ?? null },
  };
}


const _useGetPositionsFactory = Object.assign(
  (args: useGetPositionsArgs) => ({
    queryKey: ['users', 'list', args.userId, { walletPubkeys: args.walletPubkeys ?? null, market: args.market ?? null, marketPubkeys: args.marketPubkeys ?? null, outcomeMints: args.outcomeMints ?? null, positionType: args.positionType ?? null, status: args.status ?? null, includeZero: args.includeZero ?? null, sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null, limit: Number(args.limit ?? 20) }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'positionId'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetPositionsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetPositions',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetPositionsArgs, PagedData<GetPositionsRow>>;


export const useGetPositionsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/positions',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetPositionsFactory },
  parseInput: (raw: unknown): useGetPositionsArgs => raw as useGetPositionsArgs,
  parseResponse: (raw: unknown): PagedData<GetPositionsRow> => raw as PagedData<GetPositionsRow>,
  specName: 'useGetPositions' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['positions', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"positions","indexFields":["avgEntryPriceD9","costBasisD6","realizedPnlD6","realizedCostBasisD6","unrealizedPnlD6"]} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetPositionsRow>>;
export const useGetPositionsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetPositionsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetPositionsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetPositionsSubjectArgValues(value: unknown): readonly string[] {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const values = new Set<string>();
  for (const rawValue of rawValues) {
    if (typeof rawValue !== 'string') continue;
    const normalized = rawValue.trim();
    if (normalized !== '') values.add(normalized);
  }
  return [...values];
}

function _useGetPositionsHasSubjectArg(value: unknown): boolean {
  return _useGetPositionsSubjectArgValues(value).length > 0;
}

function _useGetPositionsPerEntitySubjects(args: useGetPositionsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [...(_useGetPositionsHasSubjectArg(args.market) ? [..._useGetPositionsSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.${args.market}.user.${_subjectArg0}.trade.v0`)] : []), ...(!_useGetPositionsHasSubjectArg(args.market) && _useGetPositionsHasSubjectArg(args.marketPubkeys) ? [..._useGetPositionsSubjectArgValues(args.marketPubkeys).flatMap((_subjectArg0) => _useGetPositionsSubjectArgValues(args.walletPubkeys).map((_subjectArg1) => `morfi.market.${_subjectArg0}.user.${_subjectArg1}.trade.v0`))] : []), ...(!_useGetPositionsHasSubjectArg(args.market) && !_useGetPositionsHasSubjectArg(args.marketPubkeys) ? [..._useGetPositionsSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.*.user.${_subjectArg0}.trade.v0`)] : []), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`)];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetPositionsRowSubjectTemplates(args: useGetPositionsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [...(_useGetPositionsHasSubjectArg(args.market) ? [..._useGetPositionsSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.${args.market}.user.${_subjectArg0}.trade.v0`)] : []), ...(!_useGetPositionsHasSubjectArg(args.market) && _useGetPositionsHasSubjectArg(args.marketPubkeys) ? [..._useGetPositionsSubjectArgValues(args.marketPubkeys).flatMap((_subjectArg0) => _useGetPositionsSubjectArgValues(args.walletPubkeys).map((_subjectArg1) => `morfi.market.${_subjectArg0}.user.${_subjectArg1}.trade.v0`))] : []), ...(!_useGetPositionsHasSubjectArg(args.market) && !_useGetPositionsHasSubjectArg(args.marketPubkeys) ? [..._useGetPositionsSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.*.user.${_subjectArg0}.trade.v0`)] : []), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`)];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetPositionsGatedSubjects(args: useGetPositionsArgs): readonly string[] {
  // Subjects fanned out from an ARRAY hook arg. The runtime holds each one
  // only while the entity it names is visible in some row on the page.
  const _subs: readonly string[] = [...(!_useGetPositionsHasSubjectArg(args.market) && _useGetPositionsHasSubjectArg(args.marketPubkeys) ? [..._useGetPositionsSubjectArgValues(args.marketPubkeys).flatMap((_subjectArg0) => _useGetPositionsSubjectArgValues(args.walletPubkeys).map((_subjectArg1) => `morfi.market.${_subjectArg0}.user.${_subjectArg1}.trade.v0`))] : [])];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetPositionsOnSignal(ev: MorfiEvent<unknown>): boolean {
  // Every subscription on this hook is `invalidate: true` — a matched
  // event invalidates the cached query (drop + refetch active observers)
  // via the dispatcher's `onSignal` → `invalidateQueries` path. Returns
  // `true` iff the event subject matches any declared subscription.
  const subject = ev.subject;
  if (typeof subject !== 'string') return false;
  const _patterns: readonly RegExp[] = [
    /^morfi\.market\.([^.]+)\.user\.([^.]+)\.trade\.v0$/,
    /^morfi\.market\.([^.]+)\.user\.([^.]+)\.trade\.v0$/,
    /^morfi\.market\.[^.]+\.user\.([^.]+)\.trade\.v0$/,
    /^morfi\.amm\.[^.]+\.user\.([^.]+)\.activity\.v0$/,
  ];
  for (const _p of _patterns) {
    if (_p.test(subject)) return true;
  }
  return false;
}

export const useGetPositionsRealtime = {
  domain: 'users' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.trade.v0', 'morfi.amm.*.user.*.activity.v0'] as readonly string[],
  rowKey: 'positionId' as const,
  perEntitySubjects: (args: useGetPositionsArgs) => _useGetPositionsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetPositionsArgs) => _useGetPositionsRowSubjectTemplates(args),
  gatedSubjects: (args: useGetPositionsArgs) => _useGetPositionsGatedSubjects(args),
  viewportGated: false as const,
  singleton: false as const,
  onSignal:
    (_args: useGetPositionsArgs) =>
    (ev: MorfiEvent<unknown>): boolean =>
      _useGetPositionsOnSignal(ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetPositionsRow>, unknown>;


// ─── useGetProfilePortfolioSnapshot ────────────────────────────────────────────────────────────

export interface useGetProfilePortfolioSnapshotArgs { readonly userId: string; }


async function _useGetProfilePortfolioSnapshotQueryFn(
  args: useGetProfilePortfolioSnapshotArgs,
  ctx: QueryFunctionContext,
): Promise<GetProfilePortfolioSnapshotRow | null> {
  if (args.userId === undefined || args.userId === null || args.userId === '') return null;
  const url = `/v1/users/${args.userId}/portfolio-snapshot`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetProfilePortfolioSnapshot',
    routeTemplate: '/v1/users/{userId}/portfolio-snapshot',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetProfilePortfolioSnapshotRow;
}


const _useGetProfilePortfolioSnapshotFactory = Object.assign(
  (args: useGetProfilePortfolioSnapshotArgs) => ({
    queryKey: ['users', 'detail', args.userId, 'portfolio-snapshot'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetProfilePortfolioSnapshotQueryFn(args, ctx),
    staleTime: 15000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetProfilePortfolioSnapshot',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetProfilePortfolioSnapshotArgs, GetProfilePortfolioSnapshotRow>;


export const useGetProfilePortfolioSnapshotSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/portfolio-snapshot',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetProfilePortfolioSnapshotFactory },
  parseInput: (raw: unknown): useGetProfilePortfolioSnapshotArgs => raw as useGetProfilePortfolioSnapshotArgs,
  parseResponse: (raw: unknown): GetProfilePortfolioSnapshotRow => raw as GetProfilePortfolioSnapshotRow,
  specName: 'useGetProfilePortfolioSnapshot' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetProfilePortfolioSnapshotRow>;


// ─── useGetPositionsSummary ────────────────────────────────────────────────────────────

export interface useGetPositionsSummaryArgs { readonly userId: string; readonly walletPubkeys?: readonly string[]; readonly fields?: readonly ('pubkey' | 'totalValue' | 'totalVolume' | 'totalTrades' | 'realizedPnl' | 'unrealizedPnl' | 'totalPnl' | 'marketsTraded' | 'winningPositions' | 'losingPositions' | 'totalFeesPaid' | 'holdingsCount' | 'unvaluedPositions')[]; }


async function _useGetPositionsSummaryQueryFn(
  args: useGetPositionsSummaryArgs,
  ctx: QueryFunctionContext,
): Promise<GetPositionsSummaryRow | null> {
  if (args.userId === undefined || args.userId === null || args.userId === '') return null;
  const url = `/v1/users/${args.userId}/positions-summary`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  if (args.walletPubkeys !== undefined) params.set('walletPubkeys', String(args.walletPubkeys));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetPositionsSummary',
    routeTemplate: '/v1/users/{userId}/positions-summary',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetPositionsSummaryRow;
}


const _useGetPositionsSummaryFactory = Object.assign(
  (args: useGetPositionsSummaryArgs) => ({
    queryKey: ['users', 'detail', args.userId, { walletPubkeys: args.walletPubkeys ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetPositionsSummaryQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetPositionsSummary',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetPositionsSummaryArgs, GetPositionsSummaryRow>;


export const useGetPositionsSummarySpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/positions-summary',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetPositionsSummaryFactory },
  parseInput: (raw: unknown): useGetPositionsSummaryArgs => raw as useGetPositionsSummaryArgs,
  parseResponse: (raw: unknown): GetPositionsSummaryRow => raw as GetPositionsSummaryRow,
  specName: 'useGetPositionsSummary' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetPositionsSummaryRow>;

function _useGetPositionsSummaryPerEntitySubjects(args: useGetPositionsSummaryArgs): readonly string[] {
  const subs: readonly string[] = [...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.trade.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`)];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetPositionsSummaryReconcileAlwaysSubjects(args: useGetPositionsSummaryArgs): readonly string[] {
  const _subs: readonly string[] = [...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.trade.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`)];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetPositionsSummaryApplyEvent(
  prev: GetPositionsSummaryRow | null | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetPositionsSummaryArgs,
): GetPositionsSummaryRow | null | undefined {
  // A null row (e.g. a 401 → anonymous result) has nothing to patch.
  if (prev === undefined || prev === null) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetPositionsSummaryRow = prev;
    // subject "morfi.market.${m}.user.*.trade.v0"
  {
    const _m = subject.match(/^morfi\.market\.[^.]+\.user\.([^.]+)\.trade\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['totalVolume'];
            next = { ...next, totalVolume: addD6String(_prevField, _v) } as GetPositionsSummaryRow;
          }
        }
    }
  }
  // invalidate: subject "morfi.amm.${mint}.user.*.activity.v0" → drop cache, force refetch
  if (typeof subject === 'string' && subject.match(/^morfi\.amm\.[^.]+\.user\.[^.]+\.activity\.v0$/)) { return undefined; }
  return next;
}

export const useGetPositionsSummaryRealtime = {
  domain: 'users' as const,
  domainPins: [] as readonly string[],
  rowKey: 'userId' as const,
  perEntitySubjects: (args: useGetPositionsSummaryArgs) => _useGetPositionsSummaryPerEntitySubjects(args),
  reconcileAlwaysSubjects: (args: useGetPositionsSummaryArgs) => _useGetPositionsSummaryReconcileAlwaysSubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetPositionsSummaryArgs) =>
    (prev: GetPositionsSummaryRow | null | undefined, ev: MorfiEvent<unknown>): GetPositionsSummaryRow | null | undefined =>
      _useGetPositionsSummaryApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetPositionsSummaryRow, unknown>;


// ─── useGetBackerRewards ────────────────────────────────────────────────────────────

export interface useGetBackerRewardsArgs { readonly userId: string; readonly rewardScope?: string; readonly walletPubkeysCsv?: string; readonly sortBy?: string; readonly limit?: number | string; readonly viewportGated?: boolean; readonly fields?: readonly ('marketPubkey' | 'marketTitle' | 'marketImageUrl' | 'marketState' | 'vaultD6' | 'supplyD6' | 'totalCollectedD6' | 'userShareD6' | 'backedD6' | 'backedTotalD6' | 'earnedD6' | 'claimedTotalD6' | 'canClaim')[]; }


async function _useGetBackerRewardsQueryFn(
  args: useGetBackerRewardsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetBackerRewardsRow>> {
  const url = `/v1/users/${args.userId}/backer-rewards`;
  const params = new URLSearchParams();

  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  params.set('limit', String(limitOverride ?? Number(args.limit ?? 100)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetBackerRewards',
    routeTemplate: '/v1/users/{userId}/backer-rewards',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetBackerRewardsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetBackerRewardsFactory = Object.assign(
  (args: useGetBackerRewardsArgs) => ({
    queryKey: ['users', 'list', args.userId, { sortBy: args.sortBy ?? null, limit: Number(args.limit ?? 100) }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'marketPubkey'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetBackerRewardsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: 'always' as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetBackerRewards',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetBackerRewardsArgs, PagedData<GetBackerRewardsRow>>;


export const useGetBackerRewardsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/backer-rewards',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetBackerRewardsFactory },
  parseInput: (raw: unknown): useGetBackerRewardsArgs => raw as useGetBackerRewardsArgs,
  parseResponse: (raw: unknown): PagedData<GetBackerRewardsRow> => raw as PagedData<GetBackerRewardsRow>,
  specName: 'useGetBackerRewards' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['backer-rewards', 'viewer', 'unbound', id] as readonly unknown[],
  entityStore: { resource: 'backer-rewards', scopeArg: 'rewardScope' } as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetBackerRewardsRow>>;
export const useGetBackerRewardsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 100,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetBackerRewardsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetBackerRewardsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetBackerRewardsPerEntitySubjects(_args: useGetBackerRewardsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.backer_fee_claim.v0', 'morfi.market.*.user.*.backing.v0'];
  return [...new Set(_subs)];
}

function _useGetBackerRewardsRowSubjectTemplates(_args: useGetBackerRewardsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = ['morfi.market.{pubkey}.user.*.trade.v0', 'morfi.market.{pubkey}.backer_fee_claim.v0', 'morfi.market.{pubkey}.user.*.backing.v0'];
  return _tmpls;
}

function _useGetBackerRewardsApplyEvent(
  prev: PagedData<GetBackerRewardsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetBackerRewardsArgs,
): PagedData<GetBackerRewardsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { marketPubkey?: string }).marketPubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetBackerRewardsRow;
      let updated: GetBackerRewardsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['vaultD6'];
            updated = { ...updated, vaultD6: addD6String(_prevField, _v) } as GetBackerRewardsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['totalCollectedD6'];
            updated = { ...updated, totalCollectedD6: addD6String(_prevField, _v) } as GetBackerRewardsRow;
          }
        }
        updated = (HELPERS.recomputeBackerEarnings as unknown as (prev: GetBackerRewardsRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetBackerRewardsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.backer_fee_claim.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.backer_fee_claim\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { marketPubkey?: string }).marketPubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetBackerRewardsRow;
      let updated: GetBackerRewardsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(BackerFeeClaimEventSchema, _d)
            : fromBinary(BackerFeeClaimEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof BackerFeeClaimEventSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['vaultBalanceAfterD6']) ?? ((raw as Record<string, unknown> | undefined)?.['vaultBalanceAfterD6']));
          if (_v !== undefined) {
            updated = { ...updated, vaultD6: String(_v) } as GetBackerRewardsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['supplyAfterD6']) ?? ((raw as Record<string, unknown> | undefined)?.['supplyAfterD6']));
          if (_v !== undefined) {
            updated = { ...updated, supplyD6: String(_v) } as GetBackerRewardsRow;
          }
        }
        updated = (HELPERS.applyClaimToBackerEarnings as unknown as (prev: GetBackerRewardsRow, raw: ReturnType<typeof fromBinary<typeof BackerFeeClaimEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetBackerRewardsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        updated = (HELPERS.recomputeBackerEarnings as unknown as (prev: GetBackerRewardsRow, raw: ReturnType<typeof fromBinary<typeof BackerFeeClaimEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetBackerRewardsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.user.*.backing.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.backing\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { marketPubkey?: string }).marketPubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetBackerRewardsRow;
      let updated: GetBackerRewardsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBackingSchema, _d)
            : fromBinary(MarketBackingSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBackingSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['sharesDeltaD6']) ?? ((raw as Record<string, unknown> | undefined)?.['sharesDeltaD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['supplyD6'];
            updated = { ...updated, supplyD6: addD6String(_prevField, _v) } as GetBackerRewardsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['sharesDeltaD6']) ?? ((raw as Record<string, unknown> | undefined)?.['sharesDeltaD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['backedTotalD6'];
            updated = { ...updated, backedTotalD6: addD6String(_prevField, _v) } as GetBackerRewardsRow;
          }
        }
        updated = (HELPERS.applyBackingToBackerEarnings as unknown as (prev: GetBackerRewardsRow, raw: ReturnType<typeof fromBinary<typeof MarketBackingSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetBackerRewardsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        updated = (HELPERS.recomputeBackerEarnings as unknown as (prev: GetBackerRewardsRow, raw: ReturnType<typeof fromBinary<typeof MarketBackingSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetBackerRewardsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetBackerRewardsRealtime = {
  domain: 'users' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.backer_fee_claim.v0', 'morfi.market.*.user.*.backing.v0'] as readonly string[],
  rowKey: 'marketPubkey' as const,
  perEntitySubjects: (args: useGetBackerRewardsArgs) => _useGetBackerRewardsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetBackerRewardsArgs) => _useGetBackerRewardsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetBackerRewardsArgs) =>
    (prev: PagedData<GetBackerRewardsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetBackerRewardsRow> | undefined =>
      _useGetBackerRewardsApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetBackerRewardsRow>, unknown>;


// ─── useGetBackerRewardsSummary ────────────────────────────────────────────────────────────

export interface useGetBackerRewardsSummaryArgs { readonly userId: string; readonly fields?: readonly ('totalEarnedD6' | 'claimableNowD6' | 'marketsBacked')[]; }


async function _useGetBackerRewardsSummaryQueryFn(
  args: useGetBackerRewardsSummaryArgs,
  ctx: QueryFunctionContext,
): Promise<GetBackerRewardsSummaryRow | null> {
  if (args.userId === undefined || args.userId === null || args.userId === '') return null;
  const url = `/v1/users/${args.userId}/backer-rewards/summary`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetBackerRewardsSummary',
    routeTemplate: '/v1/users/{userId}/backer-rewards/summary',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetBackerRewardsSummaryRow;
}


const _useGetBackerRewardsSummaryFactory = Object.assign(
  (args: useGetBackerRewardsSummaryArgs) => ({
    queryKey: ['users', 'detail', args.userId, 'backer-rewards/summary'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetBackerRewardsSummaryQueryFn(args, ctx),
    staleTime: 15000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetBackerRewardsSummary',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetBackerRewardsSummaryArgs, GetBackerRewardsSummaryRow>;


export const useGetBackerRewardsSummarySpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/backer-rewards/summary',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetBackerRewardsSummaryFactory },
  parseInput: (raw: unknown): useGetBackerRewardsSummaryArgs => raw as useGetBackerRewardsSummaryArgs,
  parseResponse: (raw: unknown): GetBackerRewardsSummaryRow => raw as GetBackerRewardsSummaryRow,
  specName: 'useGetBackerRewardsSummary' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetBackerRewardsSummaryRow>;


// ─── useGetClaimableRewards ────────────────────────────────────────────────────────────

export interface useGetClaimableRewardsArgs { readonly userId: string; readonly fields?: readonly ('prizeClaimableD6' | 'backerClaimableD6' | 'totalClaimableD6')[]; }


async function _useGetClaimableRewardsQueryFn(
  args: useGetClaimableRewardsArgs,
  ctx: QueryFunctionContext,
): Promise<GetClaimableRewardsRow | null> {
  if (args.userId === undefined || args.userId === null || args.userId === '') return null;
  const url = `/v1/users/${args.userId}/claimable-rewards`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetClaimableRewards',
    routeTemplate: '/v1/users/{userId}/claimable-rewards',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetClaimableRewardsRow;
}


const _useGetClaimableRewardsFactory = Object.assign(
  (args: useGetClaimableRewardsArgs) => ({
    queryKey: ['users', 'detail', args.userId, 'claimable-rewards'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetClaimableRewardsQueryFn(args, ctx),
    staleTime: 15000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetClaimableRewards',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetClaimableRewardsArgs, GetClaimableRewardsRow>;


export const useGetClaimableRewardsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/claimable-rewards',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetClaimableRewardsFactory },
  parseInput: (raw: unknown): useGetClaimableRewardsArgs => raw as useGetClaimableRewardsArgs,
  parseResponse: (raw: unknown): GetClaimableRewardsRow => raw as GetClaimableRewardsRow,
  specName: 'useGetClaimableRewards' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetClaimableRewardsRow>;


// ─── useGetUser ────────────────────────────────────────────────────────────

export interface useGetUserArgs { readonly identifier: string; readonly walletPubkeys?: readonly string[]; readonly fields?: readonly ('userId' | 'pubkey' | 'createdAt' | 'displayName' | 'handle' | 'avatarUrl' | 'avatarNsfw' | 'avatarPreferences' | 'heroUrl' | 'heroNsfw' | 'bio' | 'totalVolume' | 'totalTrades' | 'realizedPnl' | 'unrealizedPnl' | 'totalPnl' | 'marketsTraded' | 'winningPositions' | 'losingPositions' | 'totalFeesPaid' | 'l1ReferralCount' | 'firstTradeAt' | 'lastTradeAt' | 'currentStreak' | 'longestStreak' | 'streakLevel' | 'streakLastLocalDate' | 'streakTimezone' | 'wallets' | 'connectedProviders')[]; }


async function _useGetUserQueryFn(
  args: useGetUserArgs,
  ctx: QueryFunctionContext,
): Promise<GetUserRow | null> {
  if (args.identifier === undefined || args.identifier === null || args.identifier === '') return null;
  const url = `/v1/users/${args.identifier}`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.

  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetUser',
    routeTemplate: '/v1/users/{identifier}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetUserRow;
}


const _useGetUserFactory = Object.assign(
  (args: useGetUserArgs) => ({
    queryKey: ['users', 'detail', args.identifier] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetUserQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    structuralSharing: (oldData: unknown, newData: unknown) => {
      const shared = replaceEqualDeep(oldData, newData);
      // Only two profile rows merge; a null result (401 → anonymous) is taken as is.
      return oldData === undefined || oldData === null || newData === null
        ? shared
        : (HELPERS.mergeUserProfileSnapshot as (previous: GetUserRow, incoming: GetUserRow) => GetUserRow)(oldData as GetUserRow, shared as GetUserRow);
    },
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetUser',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetUserArgs, GetUserRow>;


export const useGetUserSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{identifier}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetUserFactory },
  parseInput: (raw: unknown): useGetUserArgs => raw as useGetUserArgs,
  parseResponse: (raw: unknown): GetUserRow => raw as GetUserRow,
  specName: 'useGetUser' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  publicUserEntity: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetUserRow>;

function _useGetUserPerEntitySubjects(args: useGetUserArgs): readonly string[] {
  const subs: readonly string[] = [...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.trade.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`)];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetUserApplyEvent(
  prev: GetUserRow | null | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetUserArgs,
): GetUserRow | null | undefined {
  // A null row (e.g. a 401 → anonymous result) has nothing to patch.
  if (prev === undefined || prev === null) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetUserRow = prev;
    // subject "morfi.market.${m}.user.*.trade.v0"
  {
    const _m = subject.match(/^morfi\.market\.[^.]+\.user\.([^.]+)\.trade\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        next = (HELPERS.applyWalletLifetimeTotals as unknown as (prev: GetUserRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetUserRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.amm.${mint}.user.*.activity.v0"
  {
    const _m = subject.match(/^morfi\.amm\.[^.]+\.user\.([^.]+)\.activity\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
        next = (HELPERS.applyWalletLifetimeTotals as unknown as (prev: GetUserRow, raw: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetUserRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  return next;
}

function _useGetUserRequiresAuthoritativeRefetch(
  prev: GetUserRow | null | undefined,
  ev: MorfiEvent<unknown>,
): boolean {
  // A null row is the anonymous (401) result: nothing to reconcile.
  if (prev === undefined || prev === null) return false;
  const subject = ev.subject;
  if (typeof subject !== 'string') return false;
  {
    if (/^morfi\.market\.[^.]+\.user\.([^.]+)\.trade\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        return HELPERS.streakDayChanged(prev, raw);
    }
  }
  {
    if (/^morfi\.amm\.[^.]+\.user\.([^.]+)\.activity\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
        return HELPERS.streakDayChanged(prev, raw);
    }
  }
  return false;
}

export const useGetUserRealtime = {
  domain: 'users' as const,
  domainPins: [] as readonly string[],
  rowKey: 'identifier' as const,
  perEntitySubjects: (args: useGetUserArgs) => _useGetUserPerEntitySubjects(args),
  viewportGated: false as const,
  sequenceGate: false as const,
  singleton: true as const,
  requiresAuthoritativeRefetch:
    (_args: useGetUserArgs) =>
    (prev: unknown, ev: MorfiEvent<unknown>): boolean =>
      _useGetUserRequiresAuthoritativeRefetch(prev as GetUserRow | undefined, ev),
  applyBeforeAuthoritativeRefetch: true as const,
  applyEvent:
    (args: useGetUserArgs) =>
    (prev: GetUserRow | null | undefined, ev: MorfiEvent<unknown>): GetUserRow | null | undefined =>
      _useGetUserApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetUserRow, unknown>;


// ─── useGetUserPnlChart ────────────────────────────────────────────────────────────

export interface useGetUserPnlChartArgs { readonly pubkey: string; readonly walletPubkeys?: readonly string[]; readonly from?: string; readonly to?: string; readonly range?: string; }


async function _useGetUserPnlChartQueryFn(
  args: useGetUserPnlChartArgs,
  ctx: QueryFunctionContext,
): Promise<GetUserPnlChartRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/users/${args.pubkey}/pnl-chart`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  if (args.from !== undefined) params.set('from', String(args.from));
  if (args.to !== undefined) params.set('to', String(args.to));
  if (args.range !== undefined) params.set('range', String(args.range));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetUserPnlChart',
    routeTemplate: '/v1/users/{pubkey}/pnl-chart',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetUserPnlChartRow;
}


const _useGetUserPnlChartFactory = Object.assign(
  (args: useGetUserPnlChartArgs) => ({
    queryKey: ['users', 'detail', args.pubkey, { from: args.from ?? null, to: args.to ?? null, range: args.range ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetUserPnlChartQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetUserPnlChart',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetUserPnlChartArgs, GetUserPnlChartRow>;


export const useGetUserPnlChartSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{pubkey}/pnl-chart',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetUserPnlChartFactory },
  parseInput: (raw: unknown): useGetUserPnlChartArgs => raw as useGetUserPnlChartArgs,
  parseResponse: (raw: unknown): GetUserPnlChartRow => raw as GetUserPnlChartRow,
  specName: 'useGetUserPnlChart' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetUserPnlChartRow>;

function _useGetUserPnlChartPerEntitySubjects(args: useGetUserPnlChartArgs): readonly string[] {
  const subs: readonly string[] = [...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.trade.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.claim.v0`)];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetUserPnlChartOnSignal(ev: MorfiEvent<unknown>): boolean {
  // Every subscription on this hook is `invalidate: true` — a matched
  // event invalidates the cached query (drop + refetch active observers)
  // via the dispatcher's `onSignal` → `invalidateQueries` path. Returns
  // `true` iff the event subject matches any declared subscription.
  const subject = ev.subject;
  if (typeof subject !== 'string') return false;
  const _patterns: readonly RegExp[] = [
    /^morfi\.market\.[^.]+\.user\.([^.]+)\.trade\.v0$/,
    /^morfi\.amm\.[^.]+\.user\.([^.]+)\.activity\.v0$/,
    /^morfi\.market\.[^.]+\.user\.([^.]+)\.claim\.v0$/,
  ];
  for (const _p of _patterns) {
    if (_p.test(subject)) return true;
  }
  return false;
}

export const useGetUserPnlChartRealtime = {
  domain: 'users' as const,
  domainPins: [] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetUserPnlChartArgs) => _useGetUserPnlChartPerEntitySubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  onSignal:
    (_args: useGetUserPnlChartArgs) =>
    (ev: MorfiEvent<unknown>): boolean =>
      _useGetUserPnlChartOnSignal(ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetUserPnlChartRow, unknown>;


// ─── useGetUserStreak ────────────────────────────────────────────────────────────

export interface useGetUserStreakArgs { readonly identifier: string; readonly fields?: readonly ('currentStreak' | 'longestStreak' | 'level' | 'levelName' | 'lastLocalDate' | 'timezone' | 'dayBoundaryLabel' | 'days' | 'tiers')[]; }


async function _useGetUserStreakQueryFn(
  args: useGetUserStreakArgs,
  ctx: QueryFunctionContext,
): Promise<GetUserStreakRow | null> {
  if (args.identifier === undefined || args.identifier === null || args.identifier === '') return null;
  const url = `/v1/users/${args.identifier}/streak`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetUserStreak',
    routeTemplate: '/v1/users/{identifier}/streak',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetUserStreakRow;
}


const _useGetUserStreakFactory = Object.assign(
  (args: useGetUserStreakArgs) => ({
    queryKey: ['users', 'detail', args.identifier, 'streak'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetUserStreakQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetUserStreak',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetUserStreakArgs, GetUserStreakRow>;


export const useGetUserStreakSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{identifier}/streak',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetUserStreakFactory },
  parseInput: (raw: unknown): useGetUserStreakArgs => raw as useGetUserStreakArgs,
  parseResponse: (raw: unknown): GetUserStreakRow => raw as GetUserStreakRow,
  specName: 'useGetUserStreak' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetUserStreakRow>;


// ─── useGetFollowsCounts ────────────────────────────────────────────────────────────

export interface useGetFollowsCountsArgs { readonly userId: string; readonly kind?: string; readonly fields?: readonly ('followers' | 'following')[]; }


async function _useGetFollowsCountsQueryFn(
  args: useGetFollowsCountsArgs,
  ctx: QueryFunctionContext,
): Promise<GetFollowsCountsRow | null> {
  if (args.userId === undefined || args.userId === null || args.userId === '') return null;
  const url = `/v1/users/${args.userId}/follows`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  if (args.kind !== undefined) params.set('kind', String(args.kind));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetFollowsCounts',
    routeTemplate: '/v1/users/{userId}/follows',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  // anyOf disambiguation: the data is one of N branches; the branch
  // containing the named field IS the row shape (do not unwrap further —
  // the row's own fields are the manifest's `fields:` list).
  return data as GetFollowsCountsRow;
}


const _useGetFollowsCountsFactory = Object.assign(
  (args: useGetFollowsCountsArgs) => ({
    queryKey: ['users', 'follow-counts', args.userId, { kind: args.kind ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetFollowsCountsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetFollowsCounts',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'follow-counts', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetFollowsCountsArgs, GetFollowsCountsRow>;


export const useGetFollowsCountsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/follows',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetFollowsCountsFactory },
  parseInput: (raw: unknown): useGetFollowsCountsArgs => raw as useGetFollowsCountsArgs,
  parseResponse: (raw: unknown): GetFollowsCountsRow => raw as GetFollowsCountsRow,
  specName: 'useGetFollowsCounts' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetFollowsCountsRow>;

function _useGetFollowsCountsPerEntitySubjects(args: useGetFollowsCountsArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.user.${args.userId}.notifications.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetFollowsCountsApplyEvent(
  prev: GetFollowsCountsRow | null | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetFollowsCountsArgs,
): GetFollowsCountsRow | null | undefined {
  // A null row (e.g. a 401 → anonymous result) has nothing to patch.
  if (prev === undefined || prev === null) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetFollowsCountsRow = prev;
  // Switch-based dispatcher · one parse per event, zero regex.
  if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
  const _parts = subject.split('.');
  if (_parts.length === 5 && _parts[0] === 'morfi' && _parts[1] === 'user' && _parts[3] === 'notifications') {
    switch (_parts[4]) {
      case 'v0': {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(UserNotificationSchema, _d)
            : fromBinary(UserNotificationSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof UserNotificationSchema>>;
        next = (HELPERS.incrementFollowersCount as unknown as (prev: GetFollowsCountsRow, raw: ReturnType<typeof fromBinary<typeof UserNotificationSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetFollowsCountsRow)(next, raw, args as unknown as Record<string, unknown>);
        break;
      }
      default: break;
    }
  }
  return next;
}

export const useGetFollowsCountsRealtime = {
  domain: 'users' as const,
  domainPins: [] as readonly string[],
  rowKey: 'userId' as const,
  perEntitySubjects: (args: useGetFollowsCountsArgs) => _useGetFollowsCountsPerEntitySubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetFollowsCountsArgs) =>
    (prev: GetFollowsCountsRow | null | undefined, ev: MorfiEvent<unknown>): GetFollowsCountsRow | null | undefined =>
      _useGetFollowsCountsApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetFollowsCountsRow, unknown>;


// ─── useGetFollowsCheck ────────────────────────────────────────────────────────────

export interface useGetFollowsCheckArgs { readonly userId: string; readonly kind?: string; readonly fields?: readonly ('isFollowing')[]; }


async function _useGetFollowsCheckQueryFn(
  args: useGetFollowsCheckArgs,
  ctx: QueryFunctionContext,
): Promise<GetFollowsCheckRow | null> {
  if (args.userId === undefined || args.userId === null || args.userId === '') return null;
  const url = `/v1/users/${args.userId}/follows`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  if (args.kind !== undefined) params.set('kind', String(args.kind));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetFollowsCheck',
    routeTemplate: '/v1/users/{userId}/follows',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  // anyOf disambiguation: the data is one of N branches; the branch
  // containing the named field IS the row shape (do not unwrap further —
  // the row's own fields are the manifest's `fields:` list).
  return data as GetFollowsCheckRow;
}


const _useGetFollowsCheckFactory = Object.assign(
  (args: useGetFollowsCheckArgs) => ({
    queryKey: ['users', 'detail', args.userId, { kind: args.kind ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetFollowsCheckQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetFollowsCheck',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetFollowsCheckArgs, GetFollowsCheckRow>;


export const useGetFollowsCheckSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/follows',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetFollowsCheckFactory },
  parseInput: (raw: unknown): useGetFollowsCheckArgs => raw as useGetFollowsCheckArgs,
  parseResponse: (raw: unknown): GetFollowsCheckRow => raw as GetFollowsCheckRow,
  specName: 'useGetFollowsCheck' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  viewerRelationship: {"kind":"follow","point":{"targetArg":"userId","valueField":"isFollowing"}} as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetFollowsCheckRow>;


// ─── useGetFollows ────────────────────────────────────────────────────────────

export interface useGetFollowsArgs { readonly userId: string; readonly kind?: string; readonly limit?: number | string; readonly viewportGated?: boolean; readonly fields?: readonly ('user')[]; }


async function _useGetFollowsQueryFn(
  args: useGetFollowsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetFollowsRow>> {
  const url = `/v1/users/${args.userId}/follows`;
  const params = new URLSearchParams();

  if (args.kind !== undefined) params.set('kind', String(args.kind));
  params.set('limit', String(limitOverride ?? Number(args.limit ?? 20)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetFollows',
    routeTemplate: '/v1/users/{userId}/follows',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetFollowsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetFollowsFactory = Object.assign(
  (args: useGetFollowsArgs) => ({
    queryKey: ['users', 'list', args.userId, { kind: args.kind ?? null, limit: Number(args.limit ?? 20) }, ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetFollowsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetFollows',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetFollowsArgs, PagedData<GetFollowsRow>>;


export const useGetFollowsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/follows',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetFollowsFactory },
  parseInput: (raw: unknown): useGetFollowsArgs => raw as useGetFollowsArgs,
  parseResponse: (raw: unknown): PagedData<GetFollowsRow> => raw as PagedData<GetFollowsRow>,
  specName: 'useGetFollows' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  viewerRelationship: {"kind":"follow","collection":{"rowTargetPath":"user.userId","ownerArg":"userId","ownerMatchesAuth":true,"conditionArg":"kind","conditionValue":"following"}} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetFollowsRow>>;
export const useGetFollowsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetFollowsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetFollowsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetFollowsPerEntitySubjects(args: useGetFollowsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.user.${args.userId}.notifications.v0`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetFollowsRowSubjectTemplates(args: useGetFollowsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.user.${args.userId}.notifications.v0`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetFollowsReconcileAlwaysSubjects(args: useGetFollowsArgs): readonly string[] {
  const _subs: readonly string[] = [`morfi.user.${args.userId}.notifications.v0`];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetFollowsApplyEvent(
  prev: PagedData<GetFollowsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetFollowsArgs,
): PagedData<GetFollowsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // applyEvent · morfi.user.${u}.notifications.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.user\.([^.]+)\.notifications\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(UserNotificationSchema, _d)
            : fromBinary(UserNotificationSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof UserNotificationSchema>>;
      const _next = HELPERS.keepFollowerListUntilProtectedRefetch(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetFollowsRealtime = {
  domain: 'users' as const,
  domainPins: ['morfi.user.*.notifications.v0'] as readonly string[],
  rowKey: 'user' as const,
  perEntitySubjects: (args: useGetFollowsArgs) => _useGetFollowsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetFollowsArgs) => _useGetFollowsRowSubjectTemplates(args),
  reconcileAlwaysSubjects: (args: useGetFollowsArgs) => _useGetFollowsReconcileAlwaysSubjects(args),
  viewportEntityArg: 'userId' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetFollowsArgs) =>
    (prev: PagedData<GetFollowsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetFollowsRow> | undefined =>
      _useGetFollowsApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetFollowsRow>, unknown>;


// ─── useGetUserActivity ────────────────────────────────────────────────────────────

export interface useGetUserActivityArgs { readonly userId: string; readonly walletPubkeys?: readonly string[]; readonly giftUserIds?: readonly string[]; readonly kind?: string; readonly side?: 'buy' | 'sell' | null; readonly limit?: number | string; readonly viewportGated?: boolean; readonly fields?: readonly ('marketType' | 'partnerCreatorId' | 'associatedCreator' | 'kind' | 'ts' | 'id' | 'market' | 'amountD6' | 'outcomeIndex' | 'side' | 'priceD9' | 'tradeEventId' | 'giftPda' | 'participationId' | 'opaqueId' | 'counterpartyHandle' | 'giftStatus' | 'assetKind' | 'trader' | 'outcomeLabel' | 'marketTitle' | 'marketCoverImageUrl' | 'marketCoverImageNsfw')[]; }


async function _useGetUserActivityQueryFn(
  args: useGetUserActivityArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetUserActivityRow>> {
  const url = `/v1/users/${args.userId}/activity`;
  const params = new URLSearchParams();

  if (args.kind !== undefined) params.set('kind', String(args.kind));
  if (args.side !== undefined) params.set('side', String(args.side));
  params.set('limit', String(limitOverride ?? Number(args.limit ?? 20)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetUserActivity',
    routeTemplate: '/v1/users/{userId}/activity',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetUserActivityRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetUserActivityFactory = Object.assign(
  (args: useGetUserActivityArgs) => ({
    queryKey: ['users', 'list', args.userId, { kind: args.kind ?? null, side: args.side ?? null, limit: Number(args.limit ?? 20) }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'id'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetUserActivityQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: 'always' as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetUserActivity',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetUserActivityArgs, PagedData<GetUserActivityRow>>;


export const useGetUserActivitySpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/activity',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetUserActivityFactory },
  parseInput: (raw: unknown): useGetUserActivityArgs => raw as useGetUserActivityArgs,
  parseResponse: (raw: unknown): PagedData<GetUserActivityRow> => raw as PagedData<GetUserActivityRow>,
  specName: 'useGetUserActivity' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['activity', 'viewer', 'unbound', id] as readonly unknown[],
  entityProjection: {"resource":"activity"} as const,
  entityStore: { resource: 'activity', scopeArg: 'userId' } as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetUserActivityRow>>;
export const useGetUserActivityPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetUserActivityArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetUserActivityQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetUserActivityPerEntitySubjects(args: useGetUserActivityArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.trade.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.backing.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.boost.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.claim.v0`), ...(args.giftUserIds ?? []).map((_wp) => `morfi.user.${_wp}.gift.v0`)];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetUserActivityRowSubjectTemplates(args: useGetUserActivityArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.trade.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.backing.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.boost.v0`), ...(args.walletPubkeys ?? []).map((_wp) => `morfi.market.*.user.${_wp}.claim.v0`), ...(args.giftUserIds ?? []).map((_wp) => `morfi.user.${_wp}.gift.v0`)];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetUserActivityGatedSubjects(args: useGetUserActivityArgs): readonly string[] {
  // Subjects fanned out from an ARRAY hook arg. The runtime holds each one
  // only while the entity it names is visible in some row on the page.
  const _subs: readonly string[] = [...(args.giftUserIds ?? []).map((_wp) => `morfi.user.${_wp}.gift.v0`)];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetUserActivityReconcileAlwaysSubjects(args: useGetUserActivityArgs): readonly string[] {
  const _subs: readonly string[] = [...(args.walletPubkeys ?? []).map((_wp) => `morfi.amm.*.user.${_wp}.activity.v0`)];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetUserActivityApplyEvent(
  prev: PagedData<GetUserActivityRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetUserActivityArgs,
): PagedData<GetUserActivityRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // prepend · morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.[^.]+\.user\.([^.]+)\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
      const _newRow = HELPERS.buildActivityRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetUserActivityRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['id'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Insert-only: the synthesized row is a per-trade DELTA, not a
      // full-position snapshot, so an already-present row is left
      // UNTOUCHED here (it is re-marked by the market-scoped trade helper
      // and reconciled by the next refetch) — only a brand-new id is
      // prepended. Replacing would collapse an accumulated position to the
      // single trade's quote.
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['id'] === _rowId) {
          return prev;
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // applyEvent · morfi.amm.${mint}.user.*.activity.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.amm\.[^.]+\.user\.([^.]+)\.activity\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
      const _next = HELPERS.retainActivityIndex(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // prepend · morfi.market.${m}.user.*.backing.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.[^.]+\.user\.([^.]+)\.backing\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBackingSchema, _d)
            : fromBinary(MarketBackingSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBackingSchema>>;
      const _newRow = HELPERS.buildActivityRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetUserActivityRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['id'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['id'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: _patched };
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // prepend · morfi.market.${m}.user.*.boost.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.[^.]+\.user\.([^.]+)\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
      const _newRow = HELPERS.buildActivityRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetUserActivityRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['id'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['id'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: _patched };
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // prepend · morfi.market.${m}.user.*.claim.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.[^.]+\.user\.([^.]+)\.claim\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketPrizeClaimSchema, _d)
            : fromBinary(MarketPrizeClaimSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketPrizeClaimSchema>>;
      const _newRow = HELPERS.buildActivityRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetUserActivityRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['id'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['id'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: _patched };
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // prepend · morfi.user.${u}.gift.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.user\.([^.]+)\.gift\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(GiftEventSchema, _d)
            : fromBinary(GiftEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof GiftEventSchema>>;
      const _newRow = HELPERS.buildActivityRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetUserActivityRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['id'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['id'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: _patched };
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetUserActivityRealtime = {
  domain: 'users' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.amm.*.user.*.activity.v0', 'morfi.market.*.user.*.backing.v0', 'morfi.market.*.user.*.boost.v0', 'morfi.market.*.user.*.claim.v0', 'morfi.user.*.gift.v0'] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetUserActivityArgs) => _useGetUserActivityPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetUserActivityArgs) => _useGetUserActivityRowSubjectTemplates(args),
  gatedSubjects: (args: useGetUserActivityArgs) => _useGetUserActivityGatedSubjects(args),
  reconcileAlwaysSubjects: (args: useGetUserActivityArgs) => _useGetUserActivityReconcileAlwaysSubjects(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetUserActivityArgs) =>
    (prev: PagedData<GetUserActivityRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetUserActivityRow> | undefined =>
      _useGetUserActivityApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetUserActivityRow>, unknown>;


// ─── useGetGifts ────────────────────────────────────────────────────────────

export interface useGetGiftsArgs { readonly userId: string; readonly kind?: string; readonly status?: string; readonly depositPubkeys?: string; readonly limit?: number | string; readonly viewportGated?: boolean; readonly fields?: readonly ('giftPda' | 'opaqueId' | 'recipientPlatform' | 'recipientHandle' | 'assetKind' | 'mintPubkey' | 'amount' | 'message' | 'anonymous' | 'status' | 'createdAt' | 'statusChangedAt' | 'senderHandle' | 'sender' | 'recipient')[]; }


async function _useGetGiftsQueryFn(
  args: useGetGiftsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetGiftsRow>> {
  const url = `/v1/users/${args.userId}/gifts`;
  const params = new URLSearchParams();

  if (args.kind !== undefined) params.set('kind', String(args.kind));
  if (args.status !== undefined) params.set('status', String(args.status));
  if (args.depositPubkeys !== undefined) params.set('depositPubkeys', String(args.depositPubkeys));
  params.set('limit', String(limitOverride ?? Number(args.limit ?? 20)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetGifts',
    routeTemplate: '/v1/users/{userId}/gifts',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetGiftsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetGiftsFactory = Object.assign(
  (args: useGetGiftsArgs) => ({
    queryKey: ['users', 'list', args.userId, { kind: args.kind ?? null, status: args.status ?? null, depositPubkeys: args.depositPubkeys ?? null, limit: Number(args.limit ?? 20) }, ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetGiftsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetGifts',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetGiftsArgs, PagedData<GetGiftsRow>>;


export const useGetGiftsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/gifts',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetGiftsFactory },
  parseInput: (raw: unknown): useGetGiftsArgs => raw as useGetGiftsArgs,
  parseResponse: (raw: unknown): PagedData<GetGiftsRow> => raw as PagedData<GetGiftsRow>,
  specName: 'useGetGifts' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  giftEntity: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetGiftsRow>>;
export const useGetGiftsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetGiftsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetGiftsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetGiftsPerEntitySubjects(args: useGetGiftsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.user.${args.userId}.gift.v0`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetGiftsRowSubjectTemplates(args: useGetGiftsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.user.${args.userId}.gift.v0`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetGiftsApplyEvent(
  prev: PagedData<GetGiftsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetGiftsArgs,
): PagedData<GetGiftsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // prepend · morfi.user.${u}.gift.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.user\.([^.]+)\.gift\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(GiftEventSchema, _d)
            : fromBinary(GiftEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof GiftEventSchema>>;
      const _newRow = HELPERS.buildGiftRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetGiftsRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['giftPda'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['giftPda'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: _patched };
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetGiftsRealtime = {
  domain: 'users' as const,
  domainPins: ['morfi.user.*.gift.v0'] as readonly string[],
  rowKey: 'giftPda' as const,
  perEntitySubjects: (args: useGetGiftsArgs) => _useGetGiftsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetGiftsArgs) => _useGetGiftsRowSubjectTemplates(args),
  viewportEntityArg: 'userId' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetGiftsArgs) =>
    (prev: PagedData<GetGiftsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetGiftsRow> | undefined =>
      _useGetGiftsApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetGiftsRow>, unknown>;


// ─── useGetNotificationPrefs ────────────────────────────────────────────────────────────

export interface useGetNotificationPrefsArgs { readonly fields?: readonly ('inApp')[]; }


async function _useGetNotificationPrefsQueryFn(
  args: useGetNotificationPrefsArgs,
  ctx: QueryFunctionContext,
): Promise<GetNotificationPrefsRow | null> {
  const url = '/v1/users/me/notification-prefs';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetNotificationPrefs',
    routeTemplate: '/v1/users/me/notification-prefs',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetNotificationPrefsRow;
}


const _useGetNotificationPrefsFactory = Object.assign(
  (args: useGetNotificationPrefsArgs) => ({
    queryKey: ['users', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetNotificationPrefsQueryFn(args, ctx),
    staleTime: 300000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetNotificationPrefs',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['users', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetNotificationPrefsArgs, GetNotificationPrefsRow>;


export const useGetNotificationPrefsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/me/notification-prefs',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetNotificationPrefsFactory },
  parseInput: (raw: unknown): useGetNotificationPrefsArgs => raw as useGetNotificationPrefsArgs,
  parseResponse: (raw: unknown): GetNotificationPrefsRow => raw as GetNotificationPrefsRow,
  specName: 'useGetNotificationPrefs' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetNotificationPrefsRow>;


// ─── useGetUserMarkets ────────────────────────────────────────────────────────────

export interface useGetUserMarketsArgs { readonly userId: string; readonly limit?: number | string; readonly viewportGated?: boolean; readonly fields?: readonly ('pubkey' | 'title' | 'status' | 'createdAt' | 'totalReserves' | 'volumeTotal')[]; }


async function _useGetUserMarketsQueryFn(
  args: useGetUserMarketsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetUserMarketsRow>> {
  const url = `/v1/users/${args.userId}/markets`;
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? Number(args.limit ?? 20)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetUserMarkets',
    routeTemplate: '/v1/users/{userId}/markets',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetUserMarketsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetUserMarketsFactory = Object.assign(
  (args: useGetUserMarketsArgs) => ({
    queryKey: ['users', 'list', args.userId, { limit: Number(args.limit ?? 20) }, ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetUserMarketsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetUserMarkets',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetUserMarketsArgs, PagedData<GetUserMarketsRow>>;


export const useGetUserMarketsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/markets',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetUserMarketsFactory },
  parseInput: (raw: unknown): useGetUserMarketsArgs => raw as useGetUserMarketsArgs,
  parseResponse: (raw: unknown): PagedData<GetUserMarketsRow> => raw as PagedData<GetUserMarketsRow>,
  specName: 'useGetUserMarkets' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetUserMarketsRow>>;
export const useGetUserMarketsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetUserMarketsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetUserMarketsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetUserMarketsPerEntitySubjects(_args: useGetUserMarketsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetUserMarketsRowSubjectTemplates(_args: useGetUserMarketsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetUserMarketsApplyEvent(
  prev: PagedData<GetUserMarketsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetUserMarketsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetUserMarketsRealtime = {
  domain: 'users' as const,
  domainPins: [] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetUserMarketsArgs) => _useGetUserMarketsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetUserMarketsArgs) => _useGetUserMarketsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetUserMarketsArgs) =>
    (prev: PagedData<GetUserMarketsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetUserMarketsRow> | undefined =>
      _useGetUserMarketsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetUserMarketsRow>, unknown>;


// ─── useGetCreatorStats ────────────────────────────────────────────────────────────

export interface useGetCreatorStatsArgs { readonly userId: string; }


async function _useGetCreatorStatsQueryFn(
  args: useGetCreatorStatsArgs,
  ctx: QueryFunctionContext,
): Promise<GetCreatorStatsRow | null> {
  if (args.userId === undefined || args.userId === null || args.userId === '') return null;
  const url = `/v1/users/${args.userId}/creator-stats`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCreatorStats',
    routeTemplate: '/v1/users/{userId}/creator-stats',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetCreatorStatsRow;
}


const _useGetCreatorStatsFactory = Object.assign(
  (args: useGetCreatorStatsArgs) => ({
    queryKey: ['users', 'detail', args.userId, 'creator-stats'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCreatorStatsQueryFn(args, ctx),
    staleTime: 60000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetCreatorStats',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['users', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCreatorStatsArgs, GetCreatorStatsRow>;


export const useGetCreatorStatsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/{userId}/creator-stats',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetCreatorStatsFactory },
  parseInput: (raw: unknown): useGetCreatorStatsArgs => raw as useGetCreatorStatsArgs,
  parseResponse: (raw: unknown): GetCreatorStatsRow => raw as GetCreatorStatsRow,
  specName: 'useGetCreatorStats' as const,
  polling: { hasNats: false, intervalMs: 60000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetCreatorStatsRow>;


// ─── useGetMyPayoutWallet ────────────────────────────────────────────────────────────

export interface useGetMyPayoutWalletArgs {  }


async function _useGetMyPayoutWalletQueryFn(
  args: useGetMyPayoutWalletArgs,
  ctx: QueryFunctionContext,
): Promise<GetMyPayoutWalletRow | null> {
  const url = '/v1/users/me/payout-wallet';
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMyPayoutWallet',
    routeTemplate: '/v1/users/me/payout-wallet',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetMyPayoutWalletRow;
}


const _useGetMyPayoutWalletFactory = Object.assign(
  (args: useGetMyPayoutWalletArgs) => ({
    queryKey: ['users', 'detail'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMyPayoutWalletQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetMyPayoutWallet',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['users', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMyPayoutWalletArgs, GetMyPayoutWalletRow>;


export const useGetMyPayoutWalletSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/users/me/payout-wallet',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetMyPayoutWalletFactory },
  parseInput: (raw: unknown): useGetMyPayoutWalletArgs => raw as useGetMyPayoutWalletArgs,
  parseResponse: (raw: unknown): GetMyPayoutWalletRow => raw as GetMyPayoutWalletRow,
  specName: 'useGetMyPayoutWallet' as const,
  polling: { hasNats: false, intervalMs: 15000 } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetMyPayoutWalletRow>;
